/**
 * The browser SDK's init options, in one testable place.
 *
 * These used to live inline in index.js, where nothing could assert them. That
 * is how the v11 upgrade nearly widened what we send: v11 deleted
 * `sendDefaultPii`, so the `sendDefaultPii: false` line became dead config and
 * the SDK's own, more permissive defaults would have applied. Nothing errors,
 * nothing warns, and no test could see it.
 *
 * Sentry has no BAA with us. It gets the shape of an error and nothing about
 * the patient. `utils/sentryScrub.js` is the per-event half of that; this is
 * the collection half, and `__tests__/sentryOptions.test.js` pins every
 * category so a later major cannot quietly re-enable one.
 */

import { scrubEvent, scrubSpan } from './sentryScrub';

/**
 * Header and query-parameter names Sentry's own baseline denies. Kept as the
 * SDK documents them, so a reader can compare this file against the migration
 * guide line by line.
 */
const SENSITIVE_KEY_DENYLIST = ['forwarded', '-ip', 'remote-', 'via', '-user'];

/**
 * What the SDK may collect. Every category is stated, including the ones whose
 * v11 default already matches what we want: an unset category is the SDK's
 * choice, and the SDK's choice changed once already.
 *
 * Mostly this reproduces the v10 behaviour of `sendDefaultPii: false`. Two
 * categories are deliberately tighter than that baseline:
 *
 *   urlQueryParams      v10 collected these. The diagnosis-code lookup sends
 *                       the user's typed search term as `?q=`, and in v11 the
 *                       document URL also lands on the `url.full` span
 *                       attribute, which `beforeSend` never sees. Off.
 *   stackFrameVariables v10 collected these and `scrubEvent` deleted them from
 *                       every frame. Not collecting them is the same outcome
 *                       one step earlier.
 */
const DATA_COLLECTION = {
  userInfo: false,
  cookies: false,
  httpBodies: [],
  httpHeaders: {
    request: { deny: SENSITIVE_KEY_DENYLIST },
    response: { deny: SENSITIVE_KEY_DENYLIST },
  },
  urlQueryParams: false,
  genAI: { inputs: false, outputs: false },
  graphQL: { document: false, variables: false },
  databaseQueryData: false,
  queues: false,
  stackFrameVariables: false,
  // v10 captured seven lines of source context, v11 defaults to five. Source
  // context is our own code, not patient data, so this only keeps the v10
  // reading experience rather than guarding anything.
  frameContextLines: 7,
};

/**
 * Build the init options.
 *
 * @param {object} env - Values read from the environment by the caller.
 * @param {string} env.dsn - The Sentry DSN. The caller skips init without one.
 * @param {string} [env.environment] - Deployment name, 'dev' when unset.
 *
 * @returns {object} Options for Sentry.init.
 */
export function buildSentryOptions({ dsn, environment }) {
  return {
    dsn,
    environment: environment || 'dev',
    tracesSampleRate: 0.1,
    dataCollection: DATA_COLLECTION,
    beforeSend: scrubEvent,
    beforeSendSpan: scrubSpan,
  };
}

export default buildSentryOptions;
